// src/screens/SignUpScreen.js (Adjusted with Email/Phone Options & reCAPTCHA Fix)
import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { createProfile, markCreatingProfile } from '../utils/createProfile';
import GoogleSignInButton, { OrDivider } from '../components/GoogleSignInButton';
import { PhoneIcon, MailIcon } from '../components/Icons'; 

export default function SignUpScreen() {
    // setModal comes from useAuth: the route never passed it as a prop, so
    // any error used to crash this screen.
    const { setModal } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // --- Form State ---
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [cep, setCep] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [isOver18, setIsOver18] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // State for phone authentication flow
    const [signUpMethod, setSignUpMethod] = useState('email'); // 'email' or 'phone'
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    
    const activeCampaign = location.state?.activeCampaign;
    
    // Set up reCAPTCHA verifier once on component mount
    useEffect(() => {
        try {
            // ✅ FIX: The 'auth' object must be the THIRD argument.
            window.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', {
                'size': 'invisible',
                'callback': (response) => {
                    console.log("reCAPTCHA solved, ready to send code.");
                }
            }, auth);
        } catch (error) {
            console.error("Error setting up reCAPTCHA:", error);
            setModal({ title: "Erro de Configuração", message: "Não foi possível iniciar o verificador de segurança. Por favor, atualize a página." });
        }
    }, []);


    // --- Phone Sign-Up Handlers ---

    const handleSendVerificationCode = async () => {
        if (!phoneNumber) {
            setModal({ message: "Por favor, insira um número de telefone válido." });
            return;
        }
        setIsLoading(true);
        try {
            const appVerifier = window.recaptchaVerifier;
            const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
            window.confirmationResult = confirmationResult;
            setIsCodeSent(true);
            setModal({ title: "Código Enviado", message: `Enviamos um código de verificação para ${phoneNumber}.` });
        } catch (error) {
            console.error("Error sending verification code:", error);
            setModal({ title: "Erro ao Enviar Código", message: "Não foi possível enviar o código. Verifique o número e tente novamente." });
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCodeAndSignUp = async () => {
        if (verificationCode.length !== 6) {
            setModal({ message: "Por favor, insira o código de 6 dígitos." });
            return;
        }
        setIsLoading(true);
        try {
            markCreatingProfile(true);
            const userCredential = await window.confirmationResult.confirm(verificationCode);
            const user = userCredential.user;
            console.log("✅ Phone user signed in:", user);

            await createProfileInFirestore(user);

            // Since phone is verified, navigate them directly
            navigate('/market');

        } catch (error) {
            console.error("Error verifying code:", error);
            setModal({ title: "Código Inválido", message: "O código inserido está incorreto. Tente novamente." });
        } finally {
            markCreatingProfile(false);
            setIsLoading(false);
        }
    };


    // --- Email Sign-Up Handler ---

    const handleEmailSignUp = async () => {
        setIsLoading(true);
        try {
            markCreatingProfile(true);
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            console.log("✅ Email user created in Auth:", user);

            await createProfileInFirestore(user);

            await sendEmailVerification(user);
            console.log("✅ Verification email sent.");
            navigate('/verify-email');
        } catch (err) {
            console.error("--- ❌ Email SignUp Error ---", err);
            let message = "Ocorreu um erro ao registrar sua conta.";
            if (err.code === 'auth/email-already-in-use') message = "Este e-mail já está em uso.";
            if (err.code === 'auth/weak-password') message = "Sua senha deve ter pelo menos 6 caracteres.";
            setModal({ message });
        } finally {
            markCreatingProfile(false);
            setIsLoading(false);
        }
    };


    // --- Shared Logic ---

    // The server sets credits, role and verification from the ID token and
    // validates the campaign; we only send consent, CEP and the campaign id.
    const createProfileInFirestore = (user) =>
        createProfile(user, { cep, campaignId: activeCampaign?.id || null });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!agreedToTerms || !isOver18) {
            setModal({ message: "Você deve confirmar ter mais de 18 anos e aceitar os termos." });
            return;
        }

        if (signUpMethod === 'email') {
            handleEmailSignUp();
        }
    };


    // Google signup: consents were given above, so the profile is created
    // right after the Google popup — no second screen.
    const handleGoogleSignedIn = async (user) => {
        await createProfileInFirestore(user); // idempotent for returning users
    };

    const hasConsent = isOver18 && agreedToTerms;

    // --- Render ---
    return (
        <div className="flex flex-col justify-center items-center min-h-screen p-6 bg-gray-50">
            <div id="recaptcha-container" className="absolute bottom-0 left-0"></div>

            <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-xl">
                <h1 className="text-3xl font-bold text-green-700 mb-2 text-center">Crie sua Conta</h1>
                <p className="text-gray-600 mb-6 text-center">É rápido, fácil e seguro.</p>

                {/* 1. Consents first: both signup buttons depend on them. */}
                <fieldset className="space-y-3 mb-6">
                    <legend className="sr-only">Antes de começar</legend>
                    <label className="block">
                        <span className="block text-sm font-medium text-gray-700 mb-1">CEP (opcional)</span>
                        <input type="text" inputMode="numeric" autoComplete="postal-code" maxLength={9} value={cep} onChange={e => setCep(e.target.value)} placeholder="00000-000" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={isOver18} onChange={(e) => setIsOver18(e.target.checked)} className="mt-0.5 h-4 w-4 accent-green-600" />
                        <span>Confirmo que tenho 18 anos ou mais.</span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="mt-0.5 h-4 w-4 accent-green-600" />
                        <span>
                            Li e concordo com os{' '}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Termos de Uso</a>{' '}e a{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Política de Privacidade</a>.
                        </span>
                    </label>
                    {!hasConsent && (
                        <p className="text-xs text-gray-500">Marque as duas opções para continuar.</p>
                    )}
                </fieldset>

                {/* 2. Google: one click, no confirmation e-mail. */}
                <GoogleSignInButton
                    campaignId={activeCampaign?.id || null}
                    label="Criar conta com Google"
                    disabled={!hasConsent}
                    beforeSignIn={() => markCreatingProfile(true)}
                    onSignedIn={handleGoogleSignedIn}
                    afterSignIn={() => markCreatingProfile(false)}
                />
                <p className="text-xs text-gray-500 text-center mt-2">Sem e-mail de confirmação: o Google já verificou seu endereço.</p>

                <OrDivider />

                {/* 3. E-mail and password (confirmation e-mail required). */}
                {signUpMethod === 'email' && (
                    <form onSubmit={handleSubmit} className="w-full space-y-4">
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-mail" autoComplete="email" required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                        <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Senha (mínimo 6 caracteres)" autoComplete="new-password" required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                        <button type="submit" disabled={isLoading || !hasConsent} className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                            {isLoading ? 'Criando...' : 'Criar Conta com E-mail'}
                        </button>
                    </form>
                )}

                <p className="mt-6 text-center text-sm">Já tem uma conta? <button onClick={() => navigate('/login')} className="text-green-600 font-semibold hover:underline">Faça Login</button></p>
            </div>
        </div>
    );
}
